import { Component, input, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ImportsModule } from "../../../imports";
import { DetailTableData } from '../../types';
import { FilterMatchMode, FilterService, SelectItem } from 'primeng/api';


const emptyFilterName = 'Empty Values';

@Component({
  selector: 'app-detail-table',
  standalone: true,
  imports: [ImportsModule],
  templateUrl: './detail-table.component.html',
  styleUrl: './detail-table.component.css'
})
export class DetailTableComponent implements OnInit, OnChanges {
  @Input() data: DetailTableData | null = null;

  items: any[] = [];
  columns: any[] = [];
  globalFilterFields: string[] = [];
  detailTableResults: any[] = [];
  selectedValues: { [key: string]: any[] } = {};
  selectedMatchModes: { [key: string]: string } = {};
  matchModeOptions!: SelectItem[] ;
  isBusy: boolean = false;

  constructor(private filterService: FilterService) { }

  ngOnChanges(changes: SimpleChanges): void {
    this.data = changes['data'].currentValue;
    this.items = changes['data'].currentValue.items;
    this.columns = changes['data'].currentValue.columns;
    this.globalFilterFields = changes['data'].currentValue.globalFilterFields;
    this.detailTableResults = changes['data'].currentValue.detailTableResults;
  }

  ngOnInit() {
    if (this.data) {
      this.items = this.data.items;
      this.columns = this.data.columns;
      this.globalFilterFields = this.data.globalFilterFields;
      this.detailTableResults = this.data.detailTableResults;
      this.columns.forEach(col => {
        if (col.filterType === 'text') {
          this.detailTableResults.forEach(row => {
            if (row[col.field] == null) {
              row[col.field] = ' ';
            }
          });
          this.items = this.detailTableResults;
        }
      });
    }


    this.filterService.register(emptyFilterName, (value: any, filter: any): boolean => {
      if (filter === undefined || filter === null) {
          return true;
      }

      if (value === undefined || value === null) {
          return false;
      }

      return value.toString() === ' ';
  });

  this.matchModeOptions = [
      { label: 'Starts With', value: FilterMatchMode.STARTS_WITH },
      { label: 'Contains', value: FilterMatchMode.CONTAINS },
      { label: 'Ends With', value: FilterMatchMode.ENDS_WITH },
      { label: 'Equals', value: FilterMatchMode.EQUALS },
      { label: 'Not Equals', value: FilterMatchMode.NOT_EQUALS },
      { label: 'Empty', value: emptyFilterName }
  ];
  }

  handleSort(event: any) {
    const field = event.field;
    const order = event.order;
    this.items.sort((a, b) => {
      const valueA = a[field] == null ? '' : a[field];
      const valueB = b[field] == null ? '' : b[field];
      let result = 0;
      if (valueA < valueB) {
        result = -1;
      } else if (valueA > valueB) {
        result = 1;
      }
      return order * result;
    });
  }

  onMatchModeChange(event: any, field: string) {
    this.selectedMatchModes[field] = event.value;
    if (event.value === 'noFilter' || event.value === emptyFilterName) {
      // Clear the filter for the no filter or custom match mode
      this.selectedValues[field] = [];
    }
    this.filterBySelectedValues();
  }

  getColumnOptions(field: string): any[] {
    const uniqueValues = Array.from(new Set(this.detailTableResults.map((item) => {
      const finalValue = item[field] == null || '' ? 'None' : item[field];
      return finalValue;
    })));
    if(uniqueValues.length == 1 && uniqueValues[0] == null) return []
    return uniqueValues.map(value => ({ label: value , value }));
  }

  filterBySelectedValues() {
    this.items = this.detailTableResults.filter(item => {
      return Object.keys(this.selectedValues).every(field => {
        const selectedValues: any = this.selectedValues[field];
        if (selectedValues && selectedValues.length) {
          if (Array.isArray(selectedValues)) {
            return selectedValues.includes(item[field] == null ? 'None' : item[field]);
          } else {
            const matchMode = this.selectedMatchModes[field] || 'contains';
            const value = item[field] ? item[field].toString().toLowerCase() : 'none';
            const filterValue = selectedValues.toString().toLowerCase()
            if(filterValue == '[empty]') return value == ' ';
            switch (matchMode) {
              case 'contains':
                return value.includes(filterValue);
              case 'equals':
                return value === filterValue;
              case 'startsWith':
                return value.startsWith(filterValue);
              case 'endsWith':
                return value.endsWith(filterValue);
              default:
                return true;
            }
          }
        }
        return true;
      });
    });
  }

  exportExcel() {
    this.isBusy = true;
    import("xlsx").then(xlsx => {
      const worksheet = xlsx.utils.json_to_sheet(this.items);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer: any = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });
      this.saveAsExcelFile(excelBuffer, "objects");
    });
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    import("file-saver-es").then(FileSaver => {
      this.isBusy = false;
      let EXCEL_TYPE =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      let EXCEL_EXTENSION = ".xlsx";
      const data: Blob = new Blob([buffer], {
        type: EXCEL_TYPE
      });
      FileSaver.saveAs(
        data,
        fileName + EXCEL_EXTENSION
      );
    });
  };
}
